import Swal from 'sweetalert2';
import { useState, useEffect, useMemo } from 'react';
import moment from 'moment-timezone';
import { alertaGeneral } from '../../utils/alertas';
import '../../styles/Patient.css';
import { useNavigate } from 'react-router-dom';
import { useDataUser } from '../../context/dataUserContext';
import apiClient from '../../utils/axiosPetition';
import Cargando from '../../utils/Cargando';
import { colombiaSlotToUTC, parseAppointmentDate } from '../../utils/appointmentDate';

const BOGOTA = 'America/Bogota';

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MAX_DIAS_MOSTRAR = 21; // Próximas ~3 semanas con disponibilidad

/** Formato 12 horas con AM/PM en hora Colombia */
function formatHora12h(dateOrIso) {
    const m = parseAppointmentDate(dateOrIso);
    return m.isValid() ? m.format('h:mm A') : '';
}

const DoctorCalendar = ({ doctor, show, isAgendarCita = true, isProfileComplete = true, isInModal = false }) => {
    const { isAuth, role } = useDataUser();
    const [cargando, setCargando] = useState(true);
    const [diasOcupados, setDiasOcupados] = useState([]);
    const [calendarioOficina, setCalendarioOficina] = useState([]);
    const [calendarioEspecifico, setCalendarioEspecifico] = useState([]);
    const [diaSeleccionado, setDiaSeleccionado] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const applyData = (data) => {
            setCalendarioOficina(data?.calendarioOficina || []);
            setCalendarioEspecifico(data?.calendarioEspecifico || []);
            setDiasOcupados(data?.diasOcupados || []);
        };

        const loadCalendar = (url) =>
            apiClient.get(url).then((response) => {
                if (response?.data) applyData(response.data);
                setCargando(false);
            });

        const handleError = (fallbackUrl) => (err) => {
            if (fallbackUrl) {
                return apiClient.get(fallbackUrl)
                    .then((response) => {
                        if (response?.data) applyData(response.data);
                        setCargando(false);
                    })
                    .catch(() => {
                        alertaGeneral({ titulo: 'Error', messageHtml: 'No se pudo cargar la disponibilidad. Verifica tu conexión.', isError: true });
                        setCargando(false);
                    });
            }
            alertaGeneral({ titulo: 'Error', messageHtml: 'No se pudo cargar la disponibilidad. Verifica tu conexión.', isError: true });
            setCargando(false);
        };

        if (!doctor?._id) {
            setCargando(false);
            return;
        }

        const doctorId = doctor._id;
        if (!isAuth) {
            loadCalendar(`/api/home/getCalendarDoctor/${doctorId}`).catch(handleError(null));
            return;
        }
        // Paciente autenticado: intentar endpoint de paciente; si falla (ej. 401), usar endpoint público
        loadCalendar(`/api/patient/getCalendarDoctor/${doctorId}`).catch(handleError(`/api/home/getCalendarDoctor/${doctorId}`));
    }, [doctor, isAuth]);

    /** Normaliza nombre del día para coincidir con BD (quita tildes: miércoles → miercoles). */
    const getDayOfWeek = (dia) => {
        const days = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
        const normalized = (dia || '').toLowerCase().normalize('NFD').replace(/\u0300-\u036f/g, '');
        return days.indexOf(normalized);
    };

    // Agrupa horarios en zona horaria Colombia (America/Bogota)
    const slotsPorDia = useMemo(() => {
        const ocupadosMoments = (diasOcupados || [])
            .map((d) => parseAppointmentDate(d))
            .filter((m) => m.isValid());
        const now = moment().tz(BOGOTA);
        const result = {};
        const lastDay = now.clone().add(42, 'days');

        const addSlot = (key, dateKey, inicio, fin) => {
            const startIso = colombiaSlotToUTC(dateKey, inicio);
            const endIso = colombiaSlotToUTC(dateKey, fin);
            if (!startIso || !endIso) return;

            const start = parseAppointmentDate(startIso);
            if (start.isSame(now, 'day') && !start.isAfter(now)) return;

            const isOccupied = ocupadosMoments.some((occ) => occ.isSame(start, 'minute'));
            result[key].push({ start: startIso, end: endIso, isOccupied });
        };

        for (let d = now.clone().startOf('day'); !d.isAfter(lastDay, 'day'); d.add(1, 'day')) {
            const key = d.format('YYYY-MM-DD');
            result[key] = [];

            (calendarioOficina || []).forEach(({ dia, intervalos }) => {
                if (d.day() !== getDayOfWeek(dia)) return;
                intervalos.forEach(({ inicio, fin }) => addSlot(key, key, inicio, fin));
            });

            (calendarioEspecifico || []).forEach(({ dia: fechaKey, intervalos }) => {
                if (fechaKey !== key) return;
                intervalos.forEach(({ inicio, fin }) => addSlot(key, fechaKey, inicio, fin));
            });
        }

        return result;
    }, [calendarioOficina, calendarioEspecifico, diasOcupados]);

    // Días que tienen al menos un horario (disponible u ocupado) para mostrar en el calendario
    const diasConDisponibilidad = useMemo(() => {
        return Object.entries(slotsPorDia)
            .filter(([, slots]) => slots.length > 0)
            .map(([fecha]) => fecha)
            .slice(0, MAX_DIAS_MOSTRAR);
    }, [slotsPorDia]);

    // Todos los slots del día seleccionado (disponibles y ocupados) para mostrarlos; los ocupados en rojo
    const slotsDelDiaSeleccionado = useMemo(() => {
        if (!diaSeleccionado) return [];
        return (slotsPorDia[diaSeleccionado] || []);
    }, [diaSeleccionado, slotsPorDia]);

    const formatearDiaCorta = (fechaStr) => {
        const d = parseAppointmentDate(`${fechaStr}T12:00:00-05:00`);
        return `${DIAS_SEMANA[d.day()]} ${d.date()} ${MESES[d.month()]}`;
    };

    const handleSeleccionarHora = (slot) => {
        if (!isAuth) {
            alertaGeneral({
                titulo: 'Para agendar una cita, inicia sesión o regístrate',
                isInfo: true
            }).then((result) => {
                if (result.isConfirmed) navigate('/login', { state: { showRegistro: true } });
            });
            return;
        }
        if (!isAgendarCita) return;

        const inicio = formatHora12h(slot.start);
        const fin = formatHora12h(slot.end);

        Swal.fire({
            icon: 'info',
            title: 'Horario seleccionado',
            text: `${formatearDiaCorta(diaSeleccionado)} · ${inicio} - ${fin}`,
            confirmButtonText: 'Continuar',
            showCancelButton: true,
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.isConfirmed) {
                const selectedDate = {
                    doctorId: doctor._id,
                    doctor: doctor.fullName,
                    servicio: doctor.service,
                    precioServicio: doctor.priceService,
                    fecha: slot.start,
                };
                localStorage.setItem('selectedDate', JSON.stringify(selectedDate));
                navigate(`/${role}/agendar-cita`);
            }
        });
    };

    if (!show) return null;
    if (cargando) return <Cargando />;

    return (
        <div className={`calendar-simple w-full bg-white rounded-xl shadow-sm border border-slate-200 ${isInModal ? 'p-3 md:p-4' : 'p-4 md:p-5'}`}>
            <div className="space-y-6">
                {/* Paso 1: Elegir día */}
                <div>
                    <h3 className="text-base font-semibold text-slate-800 mb-1 flex items-center gap-2">
                        <span className="w-7 h-7 rounded-full bg-[#1f94c2] text-white text-sm flex items-center justify-center font-bold shrink-0">1</span>
                        Elige un día
                    </h3>
                    <p className="text-xs text-slate-500 mb-3 ml-9">Próximas 3 semanas (días con horario del profesional)</p>
                    <div className="flex flex-wrap gap-2">
                        {diasConDisponibilidad.length === 0 ? (
                            <p className="text-sm text-slate-500 py-3 px-4 bg-slate-50 rounded-xl">
                                No hay días con disponibilidad en las próximas semanas. El profesional puede no haber configurado aún su horario de atención en la plataforma; vuelve a intentar más adelante o elige otro profesional.
                            </p>
                        ) : (
                            diasConDisponibilidad.map((fecha) => {
                                const isSelected = diaSeleccionado === fecha;
                                const d = parseAppointmentDate(`${fecha}T12:00:00-05:00`);
                                const esHoy = fecha === moment().tz(BOGOTA).format('YYYY-MM-DD');
                                return (
                                    <button
                                        key={fecha}
                                        type="button"
                                        onClick={() => setDiaSeleccionado(fecha)}
                                        className={`
                                            min-w-[5rem] min-h-[3.25rem] py-3 px-3 rounded-xl text-sm font-medium transition-all border-2
                                            focus:outline-none focus:ring-2 focus:ring-[#1f94c2] focus:ring-offset-2
                                            ${isSelected 
                                                ? 'bg-[#1f94c2] text-white border-[#1f94c2] shadow-md' 
                                                : 'bg-white text-slate-700 border-slate-200 hover:border-[#1f94c2] hover:bg-[#1f94c2]/5'}
                                            ${esHoy ? 'ring-2 ring-[#1f94c2]/40' : ''}
                                        `}
                                    >
                                        <span className="block text-[10px] uppercase font-normal leading-tight opacity-90">
                                            {DIAS_SEMANA[d.day()]}
                                        </span>
                                        <span className="block leading-tight font-semibold">{d.date()} {MESES[d.month()]}</span>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Paso 2: Elegir hora */}
                <div>
                    <h3 className="text-base font-semibold text-slate-800 mb-1 flex items-center gap-2 flex-wrap">
                        <span className="w-7 h-7 rounded-full bg-[#1f94c2] text-white text-sm flex items-center justify-center font-bold shrink-0">2</span>
                        Elige un horario
                        {diaSeleccionado && (
                            <span className="text-slate-500 font-normal text-sm">
                                — {formatearDiaCorta(diaSeleccionado)}
                            </span>
                        )}
                    </h3>
                    <p className="text-xs text-slate-500 mb-3 ml-9">Verde: disponible · Rojo: ya agendado</p>

                    {!diaSeleccionado ? (
                        <p className="text-sm text-slate-500 py-3 px-4 bg-slate-50 rounded-xl">Selecciona primero un día arriba.</p>
                    ) : slotsDelDiaSeleccionado.length === 0 ? (
                        <p className="text-sm text-slate-500 py-3 px-4 bg-amber-50 rounded-xl border border-amber-100">No hay horarios este día. Elige otro día.</p>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                            {slotsDelDiaSeleccionado.map((slot, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => !slot.isOccupied && handleSeleccionarHora(slot)}
                                    disabled={slot.isOccupied}
                                    className={`min-h-[2.75rem] py-3 px-3 rounded-xl text-sm font-semibold border-2 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2
                                        ${slot.isOccupied
                                            ? 'bg-red-50 text-red-600 border-red-200 cursor-not-allowed opacity-90'
                                            : 'bg-[#0d9488]/10 text-[#0d9488] border-[#0d9488]/30 hover:bg-[#0d9488]/20 hover:border-[#0d9488]/50 focus:ring-[#0d9488]'
                                        }`}
                                >
                                    {formatHora12h(slot.start)}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {diaSeleccionado && (
                <button
                    type="button"
                    onClick={() => setDiaSeleccionado(null)}
                    className="mt-4 text-sm font-medium text-slate-500 hover:text-[#1f94c2] transition-colors focus:outline-none focus:underline"
                >
                    ← Cambiar día
                </button>
            )}
        </div>
    );
};

export default DoctorCalendar;
